import React from 'react';
import axios from '../../../axios-config.js';
import { connect } from 'react-redux';
import { closeModal } from '../../../Redux/actions/ui.js';

const accessLabel = (level) => {
  if (Number(level) === 1) return 'Superuser';
  return 'Site Admin';
};

const getApiErrorMessage = (error, fallback) => {
  const payload = error?.response?.data;
  if (payload?.error?.message) return payload.error.message;
  if (typeof payload?.error === 'string') return payload.error;
  if (payload?.message) return payload.message;
  return fallback;
};

const buildDisplayName = (admin) => {
  const fullName = [admin?.first_name, admin?.last_name].filter(Boolean).join(' ').trim();
  return fullName || admin?.email || 'Unknown user';
};

class SiteAdminsDirectory extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      deletingID: null,
      updatingID: null,
      admins: [],
      error: null,
      addUserHash: '',
      addUserEmail: '',
      addAccessLevel: '2',
      adding: false,
      searchTerm: '',
    };

    this.loadAdmins = this.loadAdmins.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
    this.handleCreateOrUpdate = this.handleCreateOrUpdate.bind(this);
    this.handleSubmitGrant = this.handleSubmitGrant.bind(this);
    this.handleAccessLevelChange = this.handleAccessLevelChange.bind(this);
  }

  componentDidMount() {
    this.loadAdmins();
  }

  loadAdmins() {
    this.setState({ loading: true, error: null });

    axios.get('/admin/site-admins')
      .then((response) => {
        this.setState({ admins: response.data || [], loading: false });
      })
      .catch((error) => {
        const message = getApiErrorMessage(error, 'Unable to load site admins.');
        this.setState({ loading: false, error: message });
      });
  }

  handleCreateOrUpdate({ userHash, email, accessLevel, onStart, onFinish }) {
    if (typeof onStart === 'function') onStart();

    const payload = {
      accessLevel,
    };

    if (userHash) payload.userHash = userHash;
    if (email) payload.email = email;

    return axios.post('/admin/site-admins', payload)
      .then((response) => {
        this.loadAdmins();
        return response;
      })
      .catch((error) => {
        const message = getApiErrorMessage(error, 'Unable to add/update site admin.');
        this.setState({ error: message });
      })
      .finally(() => {
        if (typeof onFinish === 'function') onFinish();
      });
  }

  handleSubmitGrant() {
    const userHash = String(this.state.addUserHash || '').trim();
    const email = String(this.state.addUserEmail || '').trim().toLowerCase();
    const accessLevel = Number(this.state.addAccessLevel);

    if (!userHash && !email) {
      this.setState({ error: 'Enter user email (recommended) or user hash.' });
      return;
    }

    if (![1, 2].includes(accessLevel)) {
      this.setState({ error: 'Invalid access level selected.' });
      return;
    }

    this.handleCreateOrUpdate({
      userHash,
      email,
      accessLevel,
      onStart: () => this.setState({ adding: true, error: null }),
      onFinish: () => this.setState({ adding: false, addUserHash: '', addUserEmail: '' }),
    });
  }

  handleAccessLevelChange(admin, nextAccessLevel) {
    if (!admin?.id) return;

    const level = Number(nextAccessLevel);
    if (![1, 2].includes(level)) return;

    if (Number(admin.access_level) === level) {
      return;
    }

    this.handleCreateOrUpdate({
      userHash: admin.id,
      accessLevel: level,
      onStart: () => this.setState({ updatingID: admin.id, error: null }),
      onFinish: () => this.setState({ updatingID: null }),
    });
  }

  handleRemove(admin) {
    if (!admin || !admin.id) return;

    const label = buildDisplayName(admin);
    if (!window.confirm(`Remove site-admin access for ${label}?`)) {
      return;
    }

    this.setState({ deletingID: admin.id, error: null });

    axios.delete(`/admin/site-admins/${admin.id}`)
      .then(() => this.loadAdmins())
      .catch((error) => {
        const message = getApiErrorMessage(error, 'Unable to remove site admin.');
        this.setState({ deletingID: null, error: message });
      });
  }

  renderStats(admins) {
    const total = admins.length;
    const superusers = admins.filter((admin) => Number(admin.access_level) === 1).length;
    const siteAdmins = total - superusers;

    return (
      <div className='SiteAdminStats'>
        <div className='SiteAdminStatChip'>Total: <span>{total}</span></div>
        <div className='SiteAdminStatChip super'>Superusers: <span>{superusers}</span></div>
        <div className='SiteAdminStatChip admin'>Site admins: <span>{siteAdmins}</span></div>
      </div>
    );
  }

  render() {
    const searchValue = String(this.state.searchTerm || '').trim().toLowerCase();
    const filteredAdmins = (this.state.admins || []).filter((admin) => {
      if (!searchValue) return true;
      const display = buildDisplayName(admin).toLowerCase();
      const email = String(admin.email || '').toLowerCase();
      return display.includes(searchValue) || email.includes(searchValue);
    });

    return (
      <div className='ProjectDirectoryContainer siteAdminsDirectory'>
        <div className='ProjectDirectoryHeader SiteAdminHeader'>
          <button type='button' className='EditorHeaderButton SiteAdminHeaderButton' onClick={this.loadAdmins} title='Reload site admins' aria-label='Reload site admins'>
            <i className='material-icons notranslate'>refresh</i>
          </button>
          <div className='ProjectDirectoryTitle'>Global Admin Access</div>
          <button type='button' className='EditorHeaderButton SiteAdminHeaderButton' onClick={this.props.closeModal} aria-label='Close site admins directory'>
            <i className='material-icons notranslate'>close</i>
          </button>
        </div>

        <div className='ProjectDirectoryBody'>
          <div className='SiteAdminsDirectoryIntro'>
            Superusers have global control across all companies/projects, including role, override, edit, and publish workflows.
          </div>

          {this.renderStats(this.state.admins || [])}

          <div className='CompanyEntryContainer SiteAdminEntryContainer SiteAdminGrantPanel'>
            <div className='CompanyEntryHeader SiteAdminEntryHeader'>
              <div className='SiteAdminIdentityText'>
                <div className='CompanyHeaderLink SiteAdminDisplayName'>Grant / update global admin access</div>
                <div className='SiteAdminSubLabel'>Use email for convenience, or user hash when required.</div>
              </div>
            </div>

            <div className='SiteAdminGrantGrid'>
              <div className='SiteAdminMetaRow'>
                <div className='SiteAdminMetaLabel'>Email</div>
                <input
                  className='ProjectEntryLink SiteAdminTextInput'
                  type='email'
                  value={this.state.addUserEmail}
                  onChange={(event) => this.setState({ addUserEmail: event.target.value })}
                  placeholder='name@company.com'
                />
              </div>

              <div className='SiteAdminMetaRow'>
                <div className='SiteAdminMetaLabel'>User hash</div>
                <input
                  className='ProjectEntryLink SiteAdminTextInput'
                  type='text'
                  value={this.state.addUserHash}
                  onChange={(event) => this.setState({ addUserHash: event.target.value })}
                  placeholder='Optional if email is known'
                />
              </div>

              <div className='SiteAdminMetaRow SiteAdminGrantLevelRow'>
                <div className='SiteAdminMetaLabel'>Access level</div>
                <select
                  className='ProjectEntryLink SiteAdminSelectInput'
                  value={this.state.addAccessLevel}
                  onChange={(event) => this.setState({ addAccessLevel: event.target.value })}
                >
                  <option value='2'>Site Admin</option>
                  <option value='1'>Superuser</option>
                </select>
              </div>

              <div className='SiteAdminMetaRow SiteAdminGrantActionRow'>
                <button
                  type='button'
                  className='toggleEditButton SiteAdminActionButton'
                  disabled={this.state.adding}
                  onClick={this.handleSubmitGrant}
                >
                  {this.state.adding ? 'Saving…' : 'Save access'}
                </button>
              </div>
            </div>
          </div>

          <div className='SiteAdminSearchRow'>
            <i className='material-icons notranslate'>search</i>
            <input
              type='text'
              className='SiteAdminSearchInput'
              value={this.state.searchTerm}
              placeholder='Search by name or email'
              onChange={(event) => this.setState({ searchTerm: event.target.value })}
            />
          </div>

          <div className='SiteAdminCardsScroll'>
            {this.state.loading ? <div className='CompanyListEmpty'>Loading site admins...</div> : null}
            {this.state.error ? <div className='CompanyListEmpty SiteAdminsDirectoryError'>{this.state.error}</div> : null}

            {(filteredAdmins || []).map((admin, index) => {
              const displayName = buildDisplayName(admin);
              const isSelf = (admin.id && admin.id === this.props.currentUserHash) || (admin.email && admin.email === this.props.currentEmail);
              const isDeleting = this.state.deletingID === admin.id;
              const isUpdating = this.state.updatingID === admin.id;

              const initials = displayName.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'U';

              return (
                <div key={admin.id || index} className='CompanyEntryContainer SiteAdminEntryContainer'>
                  <div className='CompanyEntryHeader SiteAdminEntryHeader'>
                    <div className='SiteAdminIdentity'>
                      <div className='SiteAdminAvatar'>{initials}</div>
                      <div className='SiteAdminIdentityText'>
                        <div className='CompanyHeaderLink SiteAdminDisplayName'>{displayName}</div>
                        <div className='SiteAdminSubLabel'>{isSelf ? 'Current session user' : 'Global admin account'}</div>
                      </div>
                    </div>

                    <div className='SiteAdminRowActions'>
                      <select
                        className='ProjectEntryLink SiteAdminSelectInput compact'
                        value={String(admin.access_level || '2')}
                        disabled={isUpdating}
                        onChange={(event) => this.handleAccessLevelChange(admin, event.target.value)}
                      >
                        <option value='2'>Site Admin</option>
                        <option value='1'>Superuser</option>
                      </select>

                      <button
                        type='button'
                        className={`toggleEditButton SiteAdminActionButton danger ${isSelf ? 'disabled' : ''}`}
                        disabled={isSelf || isDeleting || isUpdating}
                        onClick={() => this.handleRemove(admin)}
                      >
                        {isSelf ? 'You' : (isDeleting ? 'Removing…' : 'Remove access')}
                      </button>
                    </div>
                  </div>

                  <div className='ProjectEntryHeader SiteAdminMetaRow'>
                    <div className='SiteAdminMetaLabel'>Email</div>
                    <div className='ProjectEntryLink SiteAdminMetaValue'>{admin.email}</div>
                  </div>
                  <div className='ProjectEntryHeader SiteAdminMetaRow'>
                    <div className='SiteAdminMetaLabel'>Role</div>
                    <div className={`ProjectEntryLink SiteAdminRoleChip ${Number(admin.access_level) === 1 ? 'super' : 'admin'}`}>
                      {isUpdating ? 'Updating…' : accessLabel(admin.access_level)}
                    </div>
                  </div>
                </div>
              );
            })}

            {!this.state.loading && !this.state.error && filteredAdmins.length === 0 ? (
              <div className='CompanyListEmpty'>No matching site admins.</div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  currentEmail: (state.user.email || '').toLowerCase(),
  currentUserHash: state.user.id,
});

const mapDispatchToProps = (dispatch) => ({
  closeModal: () => dispatch(closeModal())
});

export default connect(mapStateToProps, mapDispatchToProps)(SiteAdminsDirectory);
